import * as THREE from 'three/webgpu';
import {
    GameObject,
    createRigidBody,
    createBoxCollider,
} from './GameObject.js';

const app = document.querySelector('game-app');

export class VehicleObject extends GameObject {
    constructor({
        length=3,
        width=1.5,
        height=0.5,
        wheelRadius=0.5,
    } = {}) {
        super();

        this.vehicleParams = {
            length,
            width,
            height,
            wheelRadius,
            wheelWidth: 0.3,
            suspensionRestLength: 0.2,
            density: 50,
        }

        this.updateWheelsParams();

        // Add the chassis
        const chassisGeometry = new THREE.BoxGeometry(this.vehicleParams.length, this.vehicleParams.height, this.vehicleParams.width);
        this.chassisMesh = new THREE.Mesh(chassisGeometry, new THREE.MeshBasicNodeMaterial({wireframe: true}));
        this.chassisMesh.position.set(0, this.vehicleParams.wheelRadius + this.vehicleParams.suspensionRestLength + this.vehicleParams.height / 2, 0);

        const chassisCollider = createBoxCollider(this.vehicleParams.length, this.vehicleParams.height, this.vehicleParams.width);
        chassisCollider.setDensity(this.vehicleParams.density);
        this.chassisRigiBody = createRigidBody('dynamic', [chassisCollider]);

        const chassisLink = {
            visual: this.chassisMesh,
            rigidBody: this.chassisRigiBody,
        }

        this.links.push(chassisLink);

        // Add wheels
        this.wheelGeometry = new THREE.CylinderGeometry(this.vehicleParams.wheelRadius, this.vehicleParams.wheelRadius, this.vehicleParams.wheelWidth, 6);
        this.wheelGeometry.rotateX(Math.PI / 2);

        this.wheelMaterial = new THREE.MeshBasicNodeMaterial({
            color: 0xff0000,
            wireframe: true,
        });

        const wheelMeshes = this.wheelsParams.map((wheelParams) => {
            const wheelMesh = new THREE.Mesh(this.wheelGeometry, this.wheelMaterial);
            wheelMesh.position.copy(wheelParams.position);
            wheelMesh.translateY(-wheelParams.suspensionRestLength)
            this.chassisMesh.add(wheelMesh);
            return wheelMesh;
        });

        // Create controller
        this.controller = app.world.createVehicleController(this.chassisRigiBody);

        // Add wheels to controller
        this.wheelsParams.forEach( (wheel, i) => {
            this.controller.addWheel(
                wheel.position,
                new THREE.Vector3(0, -1, 0),
                new THREE.Vector3(0, 0, 1),
                wheel.suspensionRestLength,
                wheel.radius,
            );
            this.controller.setWheelSuspensionStiffness(i, wheel.suspensionStiffness);
            this.controller.setWheelSuspensionRelaxation(i, wheel.suspensionDamping);
        });

        this.engineForce = 0.0;
        this.maxEngineForce = 350;
        this.maxBackwardEngineForce = 150;
        this.brakeForce = 0.0;
        this.steering = 0.0;
    }

    apply() {
        this.updateWheelsParams();

        this.chassisMesh.children[0].geometry.dispose();    // Free memory
        this.chassisMesh.children = []; // Remove all wheels
        // Add wheels
        this.wheelGeometry = new THREE.CylinderGeometry(this.vehicleParams.wheelRadius, this.vehicleParams.wheelRadius, this.vehicleParams.wheelWidth, 6);
        this.wheelGeometry.rotateX(Math.PI / 2);

        const wheelMeshes = this.wheelsParams.map((wheelParams) => {
            const wheelMesh = new THREE.Mesh(this.wheelGeometry, this.wheelMaterial);
            wheelMesh.position.copy(wheelParams.position);
            wheelMesh.translateY(-wheelParams.suspensionRestLength)
            this.chassisMesh.add(wheelMesh);
            return wheelMesh;
        });

        // Update controller
        this.wheelsParams.forEach( (wheel, i) => {
            this.controller.setWheelRadius(i, wheel.radius);
            this.controller.setWheelSuspensionRestLength(i, wheel.suspensionRestLength);
        });
    }

    updateWheelsParams() {
        this.wheelsParams = [
            {   // Front right
                position: new THREE.Vector3(this.vehicleParams.length * 0.3, -this.vehicleParams.height / 2, this.vehicleParams.width / 2),
                suspensionRestLength: this.vehicleParams.suspensionRestLength,
                radius: this.vehicleParams.wheelRadius,
                suspensionStiffness: 100.0,
                suspensionDamping: 5.0,
            },
            {   // Front left
                position: new THREE.Vector3(this.vehicleParams.length * 0.3, -this.vehicleParams.height / 2, -this.vehicleParams.width / 2),
                suspensionRestLength: this.vehicleParams.suspensionRestLength,
                radius: this.vehicleParams.wheelRadius,
                suspensionStiffness: 100.0,
                suspensionDamping: 5.0,
            },
            {   // Back right
                position: new THREE.Vector3(-this.vehicleParams.length * 0.3, -this.vehicleParams.height / 2, this.vehicleParams.width / 2),
                suspensionRestLength: this.vehicleParams.suspensionRestLength,
                radius: this.vehicleParams.wheelRadius,
                suspensionStiffness: 100.0,
                suspensionDamping: 5.0,
            },
            {   // Back left
                position: new THREE.Vector3(-this.vehicleParams.length * 0.3, -this.vehicleParams.height / 2, -this.vehicleParams.width / 2),
                suspensionRestLength: this.vehicleParams.suspensionRestLength,
                radius: this.vehicleParams.wheelRadius,
                suspensionStiffness: 100.0,
                suspensionDamping: 5.0,
            },
        ];
    }

    updatePhysics( timestep ) {
        this.chassisRigiBody.wakeUp();

        this.controller.setWheelEngineForce(0, this.engineForce);
        this.controller.setWheelEngineForce(1, this.engineForce);

        this.controller.setWheelBrake(0, this.brakeForce);
        this.controller.setWheelBrake(1, this.brakeForce);
        this.controller.setWheelBrake(2, this.brakeForce);
        this.controller.setWheelBrake(3, this.brakeForce);

        this.controller.setWheelSteering(0, this.steering);
        this.controller.setWheelSteering(1, this.steering);

        this.controller.updateVehicle(timestep);

        for (let i = 0; i < this.controller.numWheels(); i++) {
            const wheelAxleCs = this.controller.wheelAxleCs(i);
            // console.log(wheelAxleCs);
            const wheelPosY = this.controller.wheelChassisConnectionPointCs(i).y;
            // console.log(wheelPosY);
            const suspensionLength = this.controller.wheelSuspensionLength(i);
            // console.log(suspensionLength);
            const wheelRotation = this.controller.wheelRotation(i);
            // console.log(wheelRotation);
            const steering = this.controller.wheelSteering(i);

            this.chassisMesh.children[i].position.y = wheelPosY - suspensionLength;

            const rotationQuat = new THREE.Quaternion();
            rotationQuat.setFromAxisAngle(wheelAxleCs, -wheelRotation);

            const steeringQuat = new THREE.Quaternion();
            steeringQuat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), steering);

            this.chassisMesh.children[i].quaternion.multiplyQuaternions(steeringQuat, rotationQuat);
        }

        for (const link of this.links) {
            link.visual.position.copy( link.rigidBody.translation());
            link.visual.quaternion.copy( link.rigidBody.rotation());
        }
    }
}